import React, { useEffect, useMemo, useRef, useState } from 'react';
import { configuracionService } from '../services/api';
import ConfirmPopup from './ConfirmPopup';
import './PlantillaCertificadoEditor.css';

export const CAMPOS_PLANTILLA = [
  { key: 'nombre_estudiante', label: 'Nombre y apellido', preview: 'Juan Pérez' },
  { key: 'dni', label: 'DNI', preview: '12.345.678' },
  { key: 'curso_nombre', label: 'Nombre del curso', preview: 'Python Inicial' },
  { key: 'entidad', label: 'Entidad', preview: 'Tecno3F' },
  { key: 'fecha_dia', label: 'Día', preview: '13' },
  { key: 'fecha_mes', label: 'Mes', preview: 'septiembre' },
  { key: 'fecha_anio', label: 'Año (2 dígitos)', preview: '26' },
];

const FUENTES_SISTEMA = [
  { id: 'Helvetica', label: 'Helvetica', css: 'Helvetica, Arial, sans-serif' },
  { id: 'Helvetica-Bold', label: 'Helvetica Bold', css: 'Helvetica, Arial, sans-serif', weight: 700 },
  { id: 'Times-Roman', label: 'Times', css: '"Times New Roman", Times, serif' },
  { id: 'Times-Bold', label: 'Times Bold', css: '"Times New Roman", Times, serif', weight: 700 },
  { id: 'Courier', label: 'Courier', css: '"Courier New", Courier, monospace' },
  { id: 'Courier-Bold', label: 'Courier Bold', css: '"Courier New", Courier, monospace', weight: 700 },
  { id: 'Vera', label: 'Vera', css: 'Verdana, Geneva, sans-serif' },
  { id: 'Vera-Bold', label: 'Vera Bold', css: 'Verdana, Geneva, sans-serif', weight: 700 },
  { id: 'Vera-Italic', label: 'Vera Italic', css: 'Verdana, Geneva, sans-serif', style: 'italic' },
  { id: 'Vera-BoldItalic', label: 'Vera Bold Italic', css: 'Verdana, Geneva, sans-serif', weight: 700, style: 'italic' },
];

const defaultCampoStyle = (key) => ({
  font_size: key === 'nombre_estudiante' ? 18 : 14,
  font: key === 'nombre_estudiante' || key === 'curso_nombre' ? 'Helvetica-Bold' : 'Helvetica',
  align: 'center',
  text_case: 'as_is',
});

const aplicarTextCase = (texto, textCase) => {
  const t = texto == null ? '' : String(texto);
  if (textCase === 'upper') return t.toLocaleUpperCase('es');
  if (textCase === 'lower') return t.toLocaleLowerCase('es');
  return t;
};

const emptyForm = () => ({
  id: null,
  nombre: '',
  activo: true,
  campos: {},
  imagenFile: null,
  imagenUrl: null,
  quitarImagen: false,
});

const errorMsg = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  if (data.nombre) return Array.isArray(data.nombre) ? data.nombre[0] : String(data.nombre);
  return fallback;
};

const cssFamilyName = (fontId) => `PlantillaFont_${String(fontId || '').replace(/[^a-zA-Z0-9]/g, '_')}`;

/**
 * Modal para listar / crear / editar plantillas de certificado.
 */
const PlantillaCertificadoEditor = ({ open, onClose, onSaved }) => {
  const [lista, setLista] = useState([]);
  const [fuentesApi, setFuentesApi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [confirmEliminarFuente, setConfirmEliminarFuente] = useState({ show: false, fuente: null });
  const [confirmEliminarPlantilla, setConfirmEliminarPlantilla] = useState({ show: false, id: null });
  const [form, setForm] = useState(emptyForm());
  const [modoColocar, setModoColocar] = useState(null);
  const [campoSeleccionado, setCampoSeleccionado] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [googleFamily, setGoogleFamily] = useState('');
  const [googleWeight, setGoogleWeight] = useState('400');
  const [importandoGoogle, setImportandoGoogle] = useState(false);
  const [subiendoFuente, setSubiendoFuente] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const canvasRef = useRef(null);
  const fontInputRef = useRef(null);

  const loadFuentes = async () => {
    try {
      const data = await configuracionService.getFuentesCertificado();
      setFuentesApi(Array.isArray(data) ? data : []);
    } catch (err) {
      setFuentesApi([]);
    }
  };

  const loadLista = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await configuracionService.getPlantillasCertificado();
      setLista(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(errorMsg(err, 'Error al cargar plantillas'));
      setLista([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadLista();
      loadFuentes();
      setForm(emptyForm());
      setLocalPreview(null);
      setModoColocar(null);
      setCampoSeleccionado(null);
      setOkMsg('');
      setError('');
      setGoogleFamily('');
      setImgNatural({ w: 0, h: 0 });
    }
  }, [open]);

  useEffect(() => {
    if (!form.imagenFile) {
      setLocalPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(form.imagenFile);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [form.imagenFile]);

  const previewSrc = useMemo(() => {
    if (localPreview) return localPreview;
    if (!form.quitarImagen && form.imagenUrl) return form.imagenUrl;
    return null;
  }, [localPreview, form.imagenUrl, form.quitarImagen]);

  // Ancho de página PDF (misma lógica que el backend) para escalar tipografía 1:1
  const pdfPageWidth = useMemo(() => {
    const iw = imgNatural.w || 0;
    const ih = imgNatural.h || 0;
    if (iw <= 0 || ih <= 0) return 841.89;
    const maxW = 841.89;
    const maxH = 595.28;
    const ratio = iw / ih;
    const pageRatio = maxW / maxH;
    if (ratio >= pageRatio) return maxW;
    return maxH * ratio;
  }, [imgNatural]);

  const fuentesOpciones = useMemo(() => {
    const custom = (fuentesApi || []).filter((f) => f.origen !== 'sistema');
    const sistema = FUENTES_SISTEMA.map((f) => ({ ...f, origen: 'sistema' }));
    const extras = custom.map((f) => ({
      id: f.id,
      label: f.nombre,
      origen: f.origen,
      archivo_url: f.archivo_url,
      pk: f.pk,
      css: `"${cssFamilyName(f.id)}"`,
    }));
    return [...sistema, ...extras];
  }, [fuentesApi]);

  // @font-face para preview de fuentes personalizadas
  useEffect(() => {
    const customs = (fuentesApi || []).filter((f) => f.origen !== 'sistema' && f.archivo_url);
    if (customs.length === 0) return undefined;
    const style = document.createElement('style');
    style.setAttribute('data-plantilla-fonts', '1');
    style.textContent = customs.map((f) => {
      const family = cssFamilyName(f.id);
      const url = f.archivo_url;
      const format = url.toLowerCase().includes('.otf') ? 'opentype' : 'truetype';
      return `@font-face{font-family:"${family}";src:url("${url}") format("${format}");font-display:swap;}`;
    }).join('\n');
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, [fuentesApi]);

  useEffect(() => {
    if (!open) return undefined;
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      if (el) setCanvasWidth(el.getBoundingClientRect().width || 0);
      return undefined;
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      setCanvasWidth(w);
    });
    ro.observe(el);
    setCanvasWidth(el.getBoundingClientRect().width || 0);
    return () => ro.disconnect();
  }, [open, previewSrc]);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const el = canvasRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      setForm((prev) => ({
        ...prev,
        campos: {
          ...prev.campos,
          [dragging]: {
            ...defaultCampoStyle(dragging),
            ...(prev.campos[dragging] || {}),
            x,
            y,
          },
        },
      }));
    };
    const onUp = () => setDragging(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging]);

  const fontCssFor = (fontId) => {
    const f = fuentesOpciones.find((x) => x.id === fontId);
    if (f) {
      return {
        fontFamily: f.css,
        fontWeight: f.weight || 400,
        fontStyle: f.style || 'normal',
      };
    }
    return { fontFamily: 'Helvetica, Arial, sans-serif', fontWeight: 400, fontStyle: 'normal' };
  };

  if (!open) return null;

  const nueva = () => {
    setForm(emptyForm());
    setModoColocar(null);
    setCampoSeleccionado(null);
    setError('');
    setOkMsg('');
  };

  const editar = (p) => {
    setForm({
      id: p.id,
      nombre: p.nombre || '',
      activo: p.activo !== false,
      campos: { ...(p.campos || {}) },
      imagenFile: null,
      imagenUrl: p.imagen_url || null,
      quitarImagen: false,
    });
    setModoColocar(null);
    setCampoSeleccionado(null);
    setError('');
    setOkMsg('');
  };

  const actualizarCampoStyle = (key, patch) => {
    setForm((prev) => {
      const actual = prev.campos[key];
      if (!actual) return prev;
      return {
        ...prev,
        campos: {
          ...prev.campos,
          [key]: { ...defaultCampoStyle(key), ...actual, ...patch },
        },
      };
    });
  };

  const relCoordsFromEvent = (e) => {
    const el = canvasRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    return { x, y };
  };

  const onCanvasClick = (e) => {
    if (!modoColocar || dragging) return;
    const pos = relCoordsFromEvent(e);
    if (!pos) return;
    setForm((prev) => ({
      ...prev,
      campos: {
        ...prev.campos,
        [modoColocar]: {
          ...defaultCampoStyle(modoColocar),
          ...(prev.campos[modoColocar] || {}),
          x: pos.x,
          y: pos.y,
        },
      },
    }));
    setCampoSeleccionado(modoColocar);
    setModoColocar(null);
  };

  const startDrag = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCampoSeleccionado(key);
    setDragging(key);
    setModoColocar(null);
  };

  const quitarCampo = (key) => {
    setForm((prev) => {
      const next = { ...prev.campos };
      delete next[key];
      return { ...prev, campos: next };
    });
    if (campoSeleccionado === key) setCampoSeleccionado(null);
  };

  const onUploadFuente = async (file) => {
    if (!file) return;
    setSubiendoFuente(true);
    setError('');
    setOkMsg('');
    try {
      const data = await configuracionService.uploadFuenteCertificado({
        archivo: file,
        nombre: file.name.replace(/\.(ttf|otf)$/i, ''),
      });
      await loadFuentes();
      if (campoSeleccionado && data?.id) {
        actualizarCampoStyle(campoSeleccionado, { font: data.id });
      }
      setOkMsg(`Fuente «${data.nombre || file.name}» subida.`);
    } catch (err) {
      setError(errorMsg(err, 'Error al subir la fuente'));
    } finally {
      setSubiendoFuente(false);
    }
  };

  const onImportGoogle = async () => {
    const family = googleFamily.trim();
    if (!family) {
      setError('Indicá el nombre de la familia de Google Fonts (ej. Roboto, Montserrat).');
      return;
    }
    setImportandoGoogle(true);
    setError('');
    setOkMsg('');
    try {
      const data = await configuracionService.importFuenteGoogle({
        family,
        weight: googleWeight,
        nombre: `${family}${googleWeight === '700' ? ' Bold' : ''}`,
      });
      await loadFuentes();
      if (campoSeleccionado && data?.id) {
        actualizarCampoStyle(campoSeleccionado, { font: data.id });
      }
      setOkMsg(`Fuente «${data.nombre || family}» importada de Google Fonts.`);
      setGoogleFamily('');
    } catch (err) {
      setError(errorMsg(err, 'Error al importar desde Google Fonts'));
    } finally {
      setImportandoGoogle(false);
    }
  };

  const onDeleteFuente = (f) => {
    if (!f?.pk) return;
    setConfirmEliminarFuente({ show: true, fuente: f });
  };

  const confirmarEliminarFuente = async () => {
    const f = confirmEliminarFuente.fuente;
    setConfirmEliminarFuente({ show: false, fuente: null });
    if (!f?.pk) return;
    try {
      await configuracionService.deleteFuenteCertificado(f.pk);
      await loadFuentes();
      setOkMsg('Fuente eliminada.');
    } catch (err) {
      setError(errorMsg(err, 'Error al eliminar la fuente'));
    }
  };

  const guardar = async () => {
    const nombre = (form.nombre || '').trim();
    if (!nombre) {
      setError('Indicá un nombre para la plantilla.');
      return;
    }
    if (!previewSrc && !form.imagenFile) {
      setError('Subí una imagen de fondo para la plantilla.');
      return;
    }
    setSaving(true);
    setError('');
    setOkMsg('');
    try {
      let guardada;
      if (form.id) {
        guardada = await configuracionService.updatePlantillaCertificado(form.id, {
          nombre,
          campos: form.campos,
          activo: form.activo,
          imagen: form.imagenFile || undefined,
          quitar_imagen: form.quitarImagen,
        });
      } else {
        guardada = await configuracionService.createPlantillaCertificado({
          nombre,
          campos: form.campos,
          activo: form.activo,
          imagen: form.imagenFile,
        });
      }
      await loadLista();
      if (onSaved) onSaved();
      if (guardada?.id) {
        editar(guardada);
        setOkMsg('Plantilla guardada correctamente.');
      } else {
        nueva();
        setOkMsg('Plantilla guardada correctamente.');
      }
    } catch (err) {
      setError(errorMsg(err, 'Error al guardar la plantilla'));
    } finally {
      setSaving(false);
    }
  };

  const eliminar = (id) => {
    setConfirmEliminarPlantilla({ show: true, id });
  };

  const confirmarEliminarPlantilla = async () => {
    const id = confirmEliminarPlantilla.id;
    setConfirmEliminarPlantilla({ show: false, id: null });
    if (!id) return;
    try {
      await configuracionService.deletePlantillaCertificado(id);
      if (form.id === id) nueva();
      await loadLista();
      if (onSaved) onSaved();
      setOkMsg('Plantilla eliminada.');
    } catch (err) {
      setError(errorMsg(err, 'Error al eliminar'));
    }
  };

  const cfgSel = campoSeleccionado ? form.campos[campoSeleccionado] : null;
  const labelSel = CAMPOS_PLANTILLA.find((c) => c.key === campoSeleccionado)?.label;
  const fuentesCustom = fuentesOpciones.filter((f) => f.origen !== 'sistema');

  return (
    <div className="plantilla-cert-overlay" role="dialog" aria-modal="true" aria-labelledby="plantilla-cert-title">
      <div className="plantilla-cert-modal" onClick={(e) => e.stopPropagation()}>
        <div className="plantilla-cert-modal-header">
          <h2 id="plantilla-cert-title">Plantillas de certificado</h2>
          <button type="button" className="plantilla-cert-close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        <div className="plantilla-cert-modal-body">
          <aside className="plantilla-cert-lista">
            <div className="plantilla-cert-lista-top">
              <strong>Guardadas</strong>
              <button type="button" className="plantilla-cert-btn-secondary" onClick={nueva}>+ Nueva</button>
            </div>
            {loading ? (
              <p className="plantilla-cert-muted">Cargando…</p>
            ) : lista.length === 0 ? (
              <p className="plantilla-cert-muted">Todavía no hay plantillas.</p>
            ) : (
              <ul className="plantilla-cert-lista-ul">
                {lista.map((p) => (
                  <li key={p.id} className={form.id === p.id ? 'active' : ''}>
                    <button type="button" className="plantilla-cert-lista-item" onClick={() => editar(p)}>
                      <span>{p.nombre}</span>
                      {!p.activo && <em>inactiva</em>}
                    </button>
                    <button type="button" className="plantilla-cert-lista-del" onClick={() => eliminar(p.id)} title="Eliminar">✕</button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <div className="plantilla-cert-editor">
            {okMsg && <div className="plantilla-cert-ok">{okMsg}</div>}

            <div className="plantilla-cert-form-row">
              <label htmlFor="plantilla-cert-nombre">Nombre de la plantilla</label>
              <input
                id="plantilla-cert-nombre"
                type="text"
                value={form.nombre}
                onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                maxLength={200}
                placeholder="Ej: Plantilla Tecno3F 2026"
              />
            </div>

            <div className="plantilla-cert-form-row plantilla-cert-form-row-inline">
              <label className="plantilla-cert-check">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
                />
                Activa
              </label>
              <label className="plantilla-cert-upload-btn">
                Subir imagen
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setForm((prev) => ({ ...prev, imagenFile: file, quitarImagen: false }));
                    e.target.value = '';
                  }}
                />
              </label>
              {previewSrc && (
                <button
                  type="button"
                  className="plantilla-cert-btn-secondary"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    imagenFile: null,
                    imagenUrl: null,
                    quitarImagen: !!prev.id,
                  }))}
                >
                  Quitar imagen
                </button>
              )}
            </div>

            <div className="plantilla-cert-fuentes-box">
              <strong>Fuentes tipográficas</strong>
              <p className="plantilla-cert-muted">Subí un archivo .ttf/.otf o importá una familia de Google Fonts.</p>
              <div className="plantilla-cert-fuentes-actions">
                <label className="plantilla-cert-upload-btn">
                  {subiendoFuente ? 'Subiendo…' : 'Subir fuente'}
                  <input
                    ref={fontInputRef}
                    type="file"
                    accept=".ttf,.otf,font/ttf,font/otf"
                    hidden
                    disabled={subiendoFuente}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      onUploadFuente(file);
                      e.target.value = '';
                    }}
                  />
                </label>
                <input
                  type="text"
                  className="plantilla-cert-google-input"
                  placeholder="Google Fonts: Roboto, Montserrat…"
                  value={googleFamily}
                  onChange={(e) => setGoogleFamily(e.target.value)}
                  list="plantilla-google-fonts-sugerencias"
                />
                <datalist id="plantilla-google-fonts-sugerencias">
                  <option value="Roboto" />
                  <option value="Open Sans" />
                  <option value="Montserrat" />
                  <option value="Lato" />
                  <option value="Poppins" />
                  <option value="Oswald" />
                  <option value="Raleway" />
                  <option value="Nunito" />
                  <option value="Playfair Display" />
                </datalist>
                <select
                  value={googleWeight}
                  onChange={(e) => setGoogleWeight(e.target.value)}
                  title="Peso"
                  aria-label="Peso de la fuente"
                >
                  <option value="300">Light 300</option>
                  <option value="400">Regular 400</option>
                  <option value="500">Medium 500</option>
                  <option value="600">SemiBold 600</option>
                  <option value="700">Bold 700</option>
                </select>
                <button
                  type="button"
                  className="plantilla-cert-btn-secondary"
                  onClick={onImportGoogle}
                  disabled={importandoGoogle}
                >
                  {importandoGoogle ? 'Importando…' : 'Importar Google'}
                </button>
              </div>
              {fuentesCustom.length > 0 && (
                <ul className="plantilla-cert-fuentes-ul">
                  {fuentesCustom.map((f) => (
                    <li key={f.id}>
                      <span style={{ fontFamily: f.css }}>{f.label}</span>
                      <em>{f.origen === 'google' ? 'Google' : 'Subida'}</em>
                      <button type="button" className="plantilla-cert-lista-del" onClick={() => onDeleteFuente(f)} title="Eliminar">✕</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="plantilla-cert-chips">
              <span className="plantilla-cert-chips-label">Colocar texto:</span>
              {CAMPOS_PLANTILLA.map(({ key, label }) => {
                const colocado = !!form.campos[key];
                return (
                  <button
                    key={key}
                    type="button"
                    className={`plantilla-cert-chip${modoColocar === key ? ' placing' : ''}${colocado ? ' placed' : ''}${campoSeleccionado === key ? ' selected' : ''}`}
                    onClick={() => {
                      if (colocado) {
                        setCampoSeleccionado(key);
                        setModoColocar(null);
                      } else {
                        setModoColocar((m) => (m === key ? null : key));
                      }
                    }}
                    title={colocado ? 'Clic para editar fuente/tamaño' : 'Clic y luego clic en la imagen'}
                  >
                    {label}{colocado ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>
            {modoColocar && (
              <p className="plantilla-cert-hint">Clic en la imagen para ubicar «{CAMPOS_PLANTILLA.find((c) => c.key === modoColocar)?.label}».</p>
            )}

            {cfgSel && (
              <div className="plantilla-cert-style-box">
                <strong>Estilo: {labelSel}</strong>
                <div className="plantilla-cert-style-row">
                  <label htmlFor="plantilla-cert-font">Fuente</label>
                  <select
                    id="plantilla-cert-font"
                    value={cfgSel.font || 'Helvetica'}
                    onChange={(e) => actualizarCampoStyle(campoSeleccionado, { font: e.target.value })}
                  >
                    <optgroup label="Sistema">
                      {fuentesOpciones.filter((f) => f.origen === 'sistema').map((f) => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </optgroup>
                    {fuentesCustom.length > 0 && (
                      <optgroup label="Personalizadas">
                        {fuentesCustom.map((f) => (
                          <option key={f.id} value={f.id}>{f.label}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <label htmlFor="plantilla-cert-size">Tamaño</label>
                  <input
                    id="plantilla-cert-size"
                    type="number"
                    min={6}
                    max={96}
                    value={cfgSel.font_size ?? 14}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isNaN(n)) actualizarCampoStyle(campoSeleccionado, { font_size: Math.max(6, Math.min(96, n)) });
                    }}
                  />
                  <label htmlFor="plantilla-cert-align">Alineación</label>
                  <select
                    id="plantilla-cert-align"
                    value={cfgSel.align || 'center'}
                    onChange={(e) => actualizarCampoStyle(campoSeleccionado, { align: e.target.value })}
                  >
                    <option value="left">Izquierda</option>
                    <option value="center">Centro</option>
                    <option value="right">Derecha</option>
                  </select>
                  <label htmlFor="plantilla-cert-case">Mayúsculas</label>
                  <select
                    id="plantilla-cert-case"
                    value={cfgSel.text_case || 'as_is'}
                    onChange={(e) => actualizarCampoStyle(campoSeleccionado, { text_case: e.target.value })}
                  >
                    <option value="as_is">Como está guardado</option>
                    <option value="upper">Todo mayúsculas</option>
                    <option value="lower">Todo minúsculas</option>
                  </select>
                </div>
              </div>
            )}

            <p className="plantilla-cert-muted">
              «Entidad» usa el valor del curso (crear/editar curso). Asigná esta plantilla al curso en Certificados.
            </p>

            <div
              className={`plantilla-cert-canvas-wrap${modoColocar ? ' placing' : ''}`}
              ref={canvasRef}
              onClick={onCanvasClick}
            >
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt="Vista previa de la plantilla"
                  className="plantilla-cert-canvas-img"
                  draggable={false}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    setImgNatural({
                      w: img.naturalWidth || 0,
                      h: img.naturalHeight || 0,
                    });
                  }}
                />
              ) : (
                <div className="plantilla-cert-canvas-empty">Subí una imagen para ver la vista previa y posicionar textos.</div>
              )}
              {previewSrc && CAMPOS_PLANTILLA.map(({ key, label, preview }) => {
                const cfg = form.campos[key];
                if (!cfg) return null;
                const fcss = fontCssFor(cfg.font || 'Helvetica');
                // Escala tipografía como en el PDF (pt de página → px del preview)
                const scale = canvasWidth > 0 ? canvasWidth / pdfPageWidth : 1;
                const sizePt = Number(cfg.font_size) || 14;
                const sizePx = Math.max(9, Math.min(96, sizePt * scale));
                const selected = campoSeleccionado === key;
                return (
                  <div
                    key={key}
                    className={`plantilla-cert-marker${dragging === key ? ' dragging' : ''}${selected ? ' selected' : ''}`}
                    style={{ left: `${(cfg.x || 0) * 100}%`, top: `${(cfg.y || 0) * 100}%` }}
                    onPointerDown={(e) => startDrag(key, e)}
                    title={`${label} — arrastrá para ubicar`}
                  >
                    <span className="plantilla-cert-marker-anchor" aria-hidden="true" />
                    {(selected || dragging === key) && (
                      <span className="plantilla-cert-marker-label">{label}</span>
                    )}
                    <span
                      className="plantilla-cert-marker-text"
                      style={{
                        ...fcss,
                        fontSize: `${sizePx}px`,
                      }}
                    >
                      {aplicarTextCase(preview, cfg.text_case)}
                    </span>
                    <button
                      type="button"
                      className="plantilla-cert-marker-rm"
                      onClick={(e) => { e.stopPropagation(); quitarCampo(key); }}
                      aria-label={`Quitar ${label}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="plantilla-cert-actions">
              <button type="button" className="plantilla-cert-btn-secondary" onClick={onClose}>Cerrar</button>
              <button type="button" className="plantilla-cert-btn-primary" onClick={guardar} disabled={saving}>
                {saving ? 'Guardando…' : (form.id ? 'Guardar cambios' : 'Crear plantilla')}
              </button>
            </div>
            <p className="plantilla-cert-hint">
              Arrastrá el texto para ubicarlo. El punto azul marca la posición exacta; el tamaño del preview se escala como en el PDF.
            </p>
          </div>
        </div>
      </div>
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <ConfirmPopup
        open={confirmEliminarFuente.show}
        title="Eliminar fuente"
        message={
          confirmEliminarFuente.fuente
            ? `¿Eliminar la fuente «${confirmEliminarFuente.fuente.label || confirmEliminarFuente.fuente.nombre}»?`
            : '¿Eliminar esta fuente?'
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmarEliminarFuente}
        onCancel={() => setConfirmEliminarFuente({ show: false, fuente: null })}
      />
      <ConfirmPopup
        open={confirmEliminarPlantilla.show}
        title="Eliminar plantilla"
        message="¿Eliminar esta plantilla? Los cursos que la usen pasarán al diseño por defecto."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmarEliminarPlantilla}
        onCancel={() => setConfirmEliminarPlantilla({ show: false, id: null })}
      />
    </div>
  );
};

export default PlantillaCertificadoEditor;
